import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { ISiteCoreTeaserFromServer } from '@frontend/oxygen/shared-utils';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import * as _ from 'underscore';

import { rgyellow } from '../../../bma/constants/rg-yellow.constant';
import { BonusSuppressionService } from '../../../core/services/BonusSuppression/bonus-suppression.service';
import { IPromotionSection, IPromotionsList } from '../../../core/services/cms/models';
import { IPromotion } from '../../../core/services/cms/models/promotion/promotion.model';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { UserService } from '../../../core/services/user/user.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { AbstractOutletComponent } from '../../../shared/components/abstractOutlet/abstract-outlet.component';
import { IPromotionsSiteCoreBanner, ISpPromotion } from '../../models/sp-promotion.model';
import { PromotionsService } from '../../services/promotions/promotions.service';

@Component({
    selector: 'promotions-list',
    templateUrl: './promotions-list.component.html',
    styleUrls: ['../../assets/styles/main.scss'],
    standalone: false,
})
export class PromotionsListComponent extends AbstractOutletComponent implements OnInit, OnDestroy {
    @Input() promotions?: ISpPromotion[];
    @Input() isRetail?: boolean;
    @Input() skipGrouped?: boolean = false;
    @Input() isPromoPage?: boolean = false;

    validPromotions: ISpPromotion[];
    groupedPromotions: IPromotionsList;
    lastLoginStatus: boolean;
    sendGTM: Function;
    availableGroupedPromotions: boolean;
    siteCorePromotions: ISiteCoreTeaserFromServer[];
    userFlag: boolean = false;
    private readonly componentName = 'PromotionsListComponent';
    private readonly moduleName = rgyellow.PROMOTIONS;

    constructor(
        public promotionsService: PromotionsService,
        public pubSubService: PubSubService,
        public userService: UserService,
        public windowRef: WindowRefService,
        protected bonusSuppressionService: BonusSuppressionService,
    ) {
        super();
        this.lastLoginStatus = this.promotionsService.isUserLoggedIn();
        this.sendGTM = this.promotionsService.sendGTM;
    }

    override ngOnInit(): void {
        this.showSpinner();
        if (this.userService?.isInShopUser()) {
            this.isRetail = true;
        }
        // GetPromotions from sitecore
        this.promotionsService.getPromotionsFromSiteCore().subscribe((response: IPromotionsSiteCoreBanner[]) => {
            this.siteCorePromotions = response.length > 0 ? (response[0].teasers ?? []) : [];
            this.initPromotionsList();
        });
        if (this.promotions) {
            this.init();
        } else if (this.isPromoPage) {
            (!this.userService.username || this.userService.bppToken) && this.promotionsRequestHandler();

            this.pubSubService.subscribe(this.componentName, this.pubSubService.API.SUCCESSFUL_LOGIN, () => {
                this.promotionsRequestHandler();
            });
        } else {
            this.promotionsRequestHandler();
        }
        this.pubSubService.subscribe(this.componentName, this.pubSubService.API.USER_CLOSURE_PLAY_BREAK, (val: boolean) => {
            this.userFlag = val;
        });
    }

    promotionsRequestHandler() {
        this.getPromotionsRequest().subscribe(
            (res: IPromotionsList) => {
                this.setPromotions(res);
                this.init();
            },
            () => {
                this.showError();
            },
        );
    }

    override ngOnDestroy(): void {
        this.pubSubService.unsubscribe(this.componentName);
    }

    trackPromotionBy(index: number, promotion: ISpPromotion): string | any {
        return promotion.id;
    }

    trackGroupBy(index: number, group: IPromotionSection): string {
        return `${group.name}_${index}`;
    }

    private init(): void {
        this.pubSubService.subscribe(this.componentName, this.pubSubService.API.SESSION_LOGIN, () => {
            if (!this.bonusSuppressionService.checkIfYellowFlagDisabled(this.moduleName)) {
                this.bonusSuppressionService.navigateAwayForRGYellowCustomer();
            }
            this.getPromotionsRequest().subscribe((res: IPromotionsList) => {
                this.setPromotions(res);
                this.initPromotionsList();
            });
        });

        // Waits for logout - here is the main case - server unexpectedly log out User from App
        this.pubSubService.subscribe(this.componentName, [this.pubSubService.API.SESSION_LOGOUT], () => {
            this.initPromotionsList();
        });

        // start of receiving and setting promos
        this.initPromotionsList();
    }

    private initPromotionsList(): void {
        if (this.promotions) {
            const filteredByOfferIdPromotions = this.promotionsService.filterByOfferId(this.promotions);
            this.validPromotions = this.promotionsService.preparePromotions(filteredByOfferIdPromotions, this.siteCorePromotions);
        }

        if (this.groupedPromotions) {
            this.availableGroupedPromotions = false;

            _.each(this.groupedPromotions.promotionsBySection || [], (section) => {
                const filteredByOfferIdPromotions = this.promotionsService.filterByOfferId(section.promotions as ISpPromotion[]);

                section.availablePromotions = this.promotionsService.preparePromotions(filteredByOfferIdPromotions, this.siteCorePromotions);
                this.availableGroupedPromotions = this.availableGroupedPromotions
                    ? this.availableGroupedPromotions
                    : section.availablePromotions.length > 0;
            });
        }

        this.hideSpinner();
    }

    private setPromotions(data: IPromotionsList) {
        if (!this.skipGrouped) {
            if (data.promotionsBySection) {
                this.groupedPromotions = data;
                const isInShopUser: boolean = this.userService.isInShopUser();
                const excludedTitles = new Set(['Coral Racing Club', 'Racing Super Series', 'Football Super Series']);
                const excludedKeys = new Set(['footballsuperseries', 'coral-racing-club', 'coralracingclub', 'racingsuperseries']);
                this.groupedPromotions?.promotionsBySection?.forEach((section: IPromotionSection) => {
                    section.promotions = section.promotions.filter((promotion: IPromotion) => {
                        const isExcluded = excludedTitles.has(promotion.title) || excludedKeys.has(promotion.promoKey);
                        if (isInShopUser && isExcluded) {
                            return false;
                        }
                        return this.bonusSuppressionService.checkIfYellowFlagDisabled(this.moduleName, promotion.title);
                    });
                });
            } else {
                this.promotions = data.promotions as ISpPromotion[];
            }
        }
    }

    private getPromotionsRequest(): Observable<IPromotionsList | null> {
        if (this.isRetail) {
            return this.promotionsService.promotionsRetailData();
        }

        return this.promotionsService.isGroupBySectionsEnabled().pipe(
            mergeMap((res: boolean) => {
                return res ? this.promotionsService.promotionsGroupedData() : this.promotionsService.promotionsDigitalData();
            }),
        );
    }
    /* Redirection for RSS based on playBreaks/self exclusion /closed account -F2P */

    setTargetUriforRss(promotion: IPromotion) {
        if (promotion.promoKey == 'racingsuperseries' && this.userFlag) {
            return '/promotions/details/exclusion';
        } else {
            return '/' + promotion.targetUri;
        }
    }

    /*
     * post url to parent promo page when LCG sports loaded in iframe.
     */
    postPromoUrl(promotion: IPromotion, url?: string) {
        const origin = this.windowRef.nativeWindow.location.origin;
        const postUrl = url ? url : this.setTargetUriforRss(promotion);
        this.windowRef.nativeWindow.parent.postMessage({ iframePromoMoreInfo: origin + postUrl }, '*');
    }
}
